import { FocusMonitor } from "@angular/cdk/a11y";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  booleanAttribute,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from "@angular/forms";
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatFormFieldControl } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Subject } from "rxjs";

let nextId = 0;

@Component({
  selector: "multi-select-chips-control",
  templateUrl: "./multi-select-chips-control.component.html",
  styleUrl: "./multi-select-chips-control.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: MultiSelectChipsControlComponent,
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectChipsControlComponent),
      multi: true,
    },
  ],
  imports: [MatAutocompleteModule, MatButtonModule, MatChipsModule, MatIconModule, MatTooltipModule],
  host: {
    "[attr.aria-describedby]": "describedBy",
    "[attr.id]": "id",
  },
})
export class MultiSelectChipsControlComponent
  implements MatFormFieldControl<MultiSelectChipsDescriptor[]>, ControlValueAccessor, OnDestroy
{
  readonly id = `multi-select-chips-control-${nextId++}`;
  readonly dataInput = input<MultiSelectChipsDescriptor[]>([], { alias: "data" });
  readonly labelInput = input("Items", { alias: "label" });
  readonly ariaLabelInput = input("Items", { alias: "ariaLabel" });
  readonly placeholderInput = input("Type to search...", { alias: "placeholder" });
  readonly requiredInput = input(false, { alias: "required", transform: booleanAttribute });
  readonly disabledInput = input(false, { alias: "disabled", transform: booleanAttribute });
  readonly separatorKeysCodes = [ENTER, COMMA];
  readonly touched = signal(false);
  readonly currentDesc = signal("");
  readonly selected = signal<MultiSelectChipsDescriptor[]>([]);
  readonly disabledByCva = signal(false);
  readonly hasSelected = computed(() => this.selected().length > 0);
  readonly filteredDescriptors = computed(() => {
    const current = this.currentDesc().trimStart().toLowerCase();
    const available = this.possibleDescriptors();
    return current
      ? available.filter(item => item.desc.toLowerCase().includes(current))
      : available;
  });

  readonly stateChanges = new Subject<void>();
  controlType = "multi-select-chips-control";
  focused = false;
  describedBy = "";
  errorState = false;
  readonly ngControl: NgControl | null = null;

  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private onChange: (value: MultiSelectChipsDescriptor[]) => void = () => {};
  private onTouched: () => void = () => {};

  get empty(): boolean {
    return this.selected().length === 0 && this.currentDesc().length === 0;
  }

  get placeholder(): string {
    return this.placeholderInput();
  }

  get required(): boolean {
    return this.requiredInput();
  }

  get disabled(): boolean {
    return this.disabledInput() || this.disabledByCva();
  }

  get ariaLabel(): string {
    return this.ariaLabelInput();
  }

  get label(): string {
    return this.labelInput();
  }

  get value(): MultiSelectChipsDescriptor[] {
    return this.selected();
  }

  set value(value: MultiSelectChipsDescriptor[] | null) {
    this.selected.set(value ?? []);
    this.stateChanges.next();
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  constructor() {
    this.focusMonitor
      .monitor(this.elementRef.nativeElement, true)
      .subscribe(origin => {
        this.focused = !!origin;
        this.stateChanges.next();
        if (!origin) {
          this.markAsTouched();
        }
      });
    effect(() => {
      this.dataInput();
      this.labelInput();
      this.ariaLabelInput();
      this.placeholderInput();
      this.requiredInput();
      this.disabledInput();
      this.stateChanges.next();
    });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef.nativeElement);
  }

  writeValue(value: MultiSelectChipsDescriptor[] | null): void {
    this.value = value ?? [];
  }

  registerOnChange(fn: (value: MultiSelectChipsDescriptor[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(" ");
  }

  onContainerClick(): void {
    const input = this.elementRef.nativeElement.querySelector("input");
    input?.focus();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.currentDesc.set(target?.value ?? "");
    this.stateChanges.next();
  }

  addChip(): void {
    if (this.disabled) {
      return;
    }
    const filtered = this.filteredDescriptors();
    if (filtered.length === 1) {
      this.add(filtered[0]);
    }
  }

  addAllFiltered(): void {
    if (this.disabled) {
      return;
    }
    const nextSelection = [...this.filteredDescriptors(), ...this.selected()];
    this.selected.set(nextSelection);
    this.emitChange();
  }

  remove(desc: MultiSelectChipsDescriptor): void {
    if (this.disabled) {
      return;
    }
    const nextSelected = this.selected().filter(item => item !== desc);
    this.selected.set(nextSelected);
    this.emitChange();
  }

  autocompleteSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.disabled) {
      return;
    }
    const newDesc = event.option.value as MultiSelectChipsDescriptor;
    this.add(newDesc);
    event.option.deselect();
  }

  clearAll(): void {
    if (this.disabled) {
      return;
    }
    this.selected.set([]);
    this.emitChange();
  }

  clearCurrent(): void {
    this.currentDesc.set("");
    const input = this.elementRef.nativeElement.querySelector("input");
    if (input) {
      input.value = "";
    }
    this.stateChanges.next();
  }

  markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
      this.onTouched();
    }
  }

  private add(newDesc: MultiSelectChipsDescriptor): void {
    this.selected.set([newDesc, ...this.selected()]);
    this.clearCurrent();
    this.emitChange();
  }

  private emitChange(): void {
    this.onChange(this.selected());
    this.stateChanges.next();
  }

  private possibleDescriptors(): MultiSelectChipsDescriptor[] {
    const all = this.dataInput();
    if (!all.length) {
      return [];
    }
    const selected = this.selected();
    return all.filter(item => !selected.some(selectedItem => selectedItem.id === item.id));
  }

}

export interface MultiSelectChipsDescriptor {
  id: number;
  desc: string;
}
